import L from "leaflet";

const SNAP_DISTANCE_TOLERANCE = 8;

export const refineSnapping = (map, e, closestAsset, threshold) => {
  if (closestAsset.distance < threshold) {
    const isMarker = ["marker", "existingpole"].includes(closestAsset.asset.type);
    if (!isMarker) {
      return _checkPrioritySnapping(map, closestAsset);
    } else {
      return closestAsset.latlng;
    }
  } else {
    return { lat: e.latlng.lat, lng: e.latlng.lng };
  }
};

export const _calcClosestAsset = (
  map,
  latlng,
  assets,
  snapDistance = SNAP_DISTANCE_TOLERANCE,
  useSnapDistanceTolerance = true
) => {
  const closestAssets = [];

  // Loop through the layers
  assets.forEach((asset) => {
    const results = _calcLayerDistances(map, latlng, asset);

    // Find all assets within a certain distance of the cursor
    if (results.distance <= snapDistance || !useSnapDistanceTolerance) {
      const nearbyAsset = results;
      nearbyAsset.asset = asset;
      closestAssets.push(nearbyAsset);
    }
  });

  // Return the closest asset, prioritised by type (poles are prioritise over cables)
  // Return an empty object if no close assets exist
  return _getClosestAssetByPriority(closestAssets, snapDistance);
};
export const _calcLayerDistances = (map, latlng, asset) => {
  // is this a marker?
  const isMarker = ["marker", "existingpole"].includes(asset.type);

  // is it a polygon?
  const isPolygon =
    asset.type === "site" ||
    asset.type === "new-domestic" ||
    asset.type === "new-non-domestic" ||
    asset.type === "new-mixed";

  // the point which we want to snap (probably the marker that is dragged)
  const currentPoint = latlng;

  // the coords of the layer
  const latlngs = isMarker ? asset.markerGeometry : asset.polyGeometry;

  if (isMarker) {
    // return the info for the marker, no more calculations needed
    return {
      latlng: Object.assign({}, latlngs),
      distance: _getDistance(map, latlngs, currentPoint),
    };
  }

  // the closest segment (line between two points) of the layer
  let closestSegment;

  // the shortest distance from P to closestSegment
  let shortestDistance;

  // loop through the coords of the layer
  const loopThroughCoords = (coords) => {
    coords.forEach((coord, index) => {
      if (Array.isArray(coord)) {
        loopThroughCoords(coord);
        return;
      }

      const nextIndexLastValue = isPolygon ? 0 : undefined;
      const nextIndex = index + 1 === coords.length ? nextIndexLastValue : index + 1;

      // take this coord...
      const startCoord = coord;
      const endCoord = coords[nextIndex];

      if (endCoord) {
        // calc the distance between the current point and the segment
        const distance = _getDistanceToSegment(map, currentPoint, startCoord, endCoord);

        // is the distance shorter than the previous one? Save it and the segment
        if (shortestDistance === undefined || distance < shortestDistance) {
          shortestDistance = distance;
          closestSegment = [startCoord, endCoord];
        }
      }
    });
  };

  loopThroughCoords(latlngs);

  // now, take the closest segment (closestSegment) and calc the closest point to the current point on it.
  const closestPointOnSegment = _getClosestPointOnSegment(
    map,
    latlng,
    closestSegment[0],
    closestSegment[1]
  );

  // return the latlng of that sucker. booyakasha!
  return {
    latlng: Object.assign({}, closestPointOnSegment),
    segment: closestSegment,
    distance: shortestDistance,
  };
};

const _getClosestAssetByPriority = (assets, snapDistance = SNAP_DISTANCE_TOLERANCE) => {
  const sortedAssets = assets.sort((a, b) => a.distance - b.distance);

  // If one or more poles are within snapping distance, snap to the closest pole
  const closestPole = sortedAssets.find((a) => a.asset.type === "existingpole");
  const snapppedPole = closestPole?.distance < snapDistance ? closestPole : undefined;

  return snapppedPole ?? assets[0] ?? {};
};
// we got the point we want to snap to (C), but we need to check if a coord of the polygon
// receives priority over C as the snapping point. Let's check this here
export const _checkPrioritySnapping = (map, closestAsset) => {
  // A and B are the points of the closest segment to P (the marker position we want to snap)
  const A = closestAsset.segment[0];
  const B = closestAsset.segment[1];

  // C is the point we would snap to on the segment.
  // The closest point on the closest segment of the closest polygon to P. That's right.
  const C = closestAsset.latlng;

  // distances from A to C and B to C to check which one is closer to C
  const distanceAC = _getDistance(map, A, C);
  const distanceBC = _getDistance(map, B, C);

  // closest latlng of A and B to C
  let closestVertexLatLng = distanceAC < distanceBC ? A : B;

  // distance between closestVertexLatLng and C
  let shortestDistance = distanceAC < distanceBC ? distanceAC : distanceBC;

  // snap to middle (M) of segment if option is enabled
  //if (this.options.snapMiddle) {
  // const M = calcMiddleLatLng(map, A, B);
  // const distanceMC = _getDistance(map, M, C);

  // if (distanceMC < distanceAC && distanceMC < distanceBC) {
  //   // M is the nearest vertex
  //   closestVertexLatLng = M;
  //   shortestDistance = distanceMC;
  // }
  //}

  // the distance that needs to be undercut to trigger priority
  const priorityDistance = 20;

  // the latlng we ultemately want to snap to
  let snapLatlng;

  // if C is closer to the closestVertexLatLng (A, B or M) than the snapDistance,
  // the closestVertexLatLng has priority over C as the snapping point.
  if (shortestDistance < priorityDistance) {
    snapLatlng = closestVertexLatLng;
  } else {
    snapLatlng = C;
  }

  // return the copy of snapping point
  return Object.assign({}, snapLatlng);
};

const _getClosestPointOnSegment = (map, latlng, latlngA, latlngB) => {
  let maxzoom = map.getMaxZoom();
  if (maxzoom === Infinity) {
    maxzoom = map.getZoom();
  }
  const P = map.project(latlng, maxzoom);
  const A = map.project(latlngA, maxzoom);
  const B = map.project(latlngB, maxzoom);
  const closest = L.LineUtil.closestPointOnSegment(P, A, B);
  return map.unproject(closest, maxzoom);
};

const _getDistanceToSegment = (map, latlng, latlngA, latlngB) => {
  const P = map.latLngToLayerPoint(latlng);
  const A = map.latLngToLayerPoint(latlngA);
  const B = map.latLngToLayerPoint(latlngB);
  return L.LineUtil.pointToSegmentDistance(P, A, B);
};

const _getDistance = (map, latlngA, latlngB) => {
  return map.latLngToLayerPoint(latlngA).distanceTo(map.latLngToLayerPoint(latlngB));
};
